import { useSelector } from "react-redux";
import DashboardHeader from "../../../components/dashboard/dashboardHeader";
import { useUserHasAccess } from "../../../routes/roleOnly";
import { useState, useMemo, useCallback, useEffect } from "react";
import { EmployeeSelector } from "../../../components/utils/moduleSelectors/employeeSelector";
import { useTranslation } from "react-i18next";
import { HoursViewComponent } from "../../../components/dashboard/hours/hours_view_component";
import { useSearchParams, useParams } from "react-router-dom";
import { HourEditDialog } from "../../../components/dashboard/hours/hour_edit_dialog";

export const HourSheet = () => {
  const currentUser = useSelector((state) => state.session.currentUser);
  const [user, setUser] = useState(currentUser);
  const [searchParams, setSearchParams] = useSearchParams();
  const { id: routeHourId } = useParams();

  const isAdmin = useSelector((state) =>
    state.session.currentUser?.roles?.some((r) => r.name === "admin")
  );
  const isManager = useUserHasAccess({ role: "hours", action: "manage" });

  const userMemo = useMemo(
    () => (isAdmin || isManager ? user : currentUser),
    [user, isAdmin, isManager, currentUser]
  );
  const user_id = userMemo?.id;

  const [hourFormId, setHourFormId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newHourPrefill, setNewHourPrefill] = useState(null);
  const [lastChangedHour, setLastChangedHour] = useState(null);
  const [deletedHourId, setDeletedHourId] = useState(null);
  const [currentViewDate, setCurrentViewDate] = useState(null);
  const { t } = useTranslation();

  const hourIdParam = useMemo(() => searchParams.get("hourId"), [searchParams]);

  const clearHourIdFromUrl = useCallback(() => {
    const current = Object.fromEntries(searchParams.entries());
    if (current.hourId) {
      delete current.hourId;
      setSearchParams(current, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  // Deep link: /dashboard/hours/:id
  useEffect(() => {
    if (routeHourId && /^\d+$/.test(String(routeHourId))) {
      setHourFormId(String(routeHourId));
      setDialogOpen(true);
    }
  }, [routeHourId]);

  // Deep link: ?hourId=
  useEffect(() => {
    if (routeHourId && /^\d+$/.test(String(routeHourId))) return;
    if (hourIdParam && /^\d+$/.test(hourIdParam)) {
      setHourFormId(hourIdParam);
      setDialogOpen(true);
    }
  }, [hourIdParam, routeHourId]);

  // URL params for view and period
  const initialViewParam = useMemo(
    () => searchParams.get("view") || "day",
    [searchParams]
  );
  const initialDateParam = useMemo(
    () => searchParams.get("date") || null,
    [searchParams]
  );
  const initialFromParam = useMemo(
    () => searchParams.get("from") || null,
    [searchParams]
  );
  const initialToParam = useMemo(
    () => searchParams.get("to") || null,
    [searchParams]
  );
  const initialPeriodParam = useMemo(
    () => searchParams.get("period") || "month",
    [searchParams]
  );

  const updateUrlParams = useCallback(
    (updates) => {
      const current = Object.fromEntries(searchParams.entries());
      const next = { ...current, ...updates };
      if (next.view === "day" || next.view === "week") {
        delete next.from;
        delete next.to;
        if (!next.date) delete next.date;
      }
      if (next.view === "period") {
        delete next.date;
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handleHourClick = useCallback(
    (hourId) => {
      setHourFormId(hourId);
      setNewHourPrefill(null);
      setDialogOpen(true);
      updateUrlParams({ hourId });
    },
    [updateUrlParams]
  );

  const handleNewHour = useCallback(
    (date = null) => {
      setHourFormId(null);
      setDialogOpen(true);
      const d =
        date ||
        currentViewDate ||
        initialDateParam ||
        new Date().toISOString().substring(0, 10);
      setNewHourPrefill({
        date: d,
        user_id: userMemo?.id,
        user: userMemo,
      });
    },
    [currentViewDate, initialDateParam, userMemo]
  );

  const closeDialog = useCallback(() => {
    setDialogOpen(false);
    setHourFormId(null);
    setNewHourPrefill(null);
    clearHourIdFromUrl();
  }, [clearHourIdFromUrl]);

  const handleSaved = useCallback(
    (saved) => {
      setLastChangedHour(saved);
      closeDialog();
    },
    [closeDialog]
  );

  const handleDeleted = useCallback(
    (id) => {
      setDeletedHourId(id);
      setTimeout(() => setDeletedHourId(null), 0);
      closeDialog();
    },
    [closeDialog]
  );

  return (
    <DashboardHeader title={t("Timeliste")}>
      <div className="border-1 border-blue-400 px-4 py-4 grid grid-cols-1 gap-4">
        <div className="bg-white px-4 py-5 sm:px-6 rounded-md shadow flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            {isAdmin || isManager ? (
              <EmployeeSelector
                name="user_id"
                selected={user}
                filters={[{ name: "employee", value: true }]}
                onChange={(data) => {
                  setUser(data?.target?.object);
                  setHourFormId(null);
                  setDialogOpen(false);
                  if (data?.target?.object?.id) {
                    updateUrlParams({ user_id: data.target.object.id });
                  }
                }}
              />
            ) : (
              <span>{user?.displayTitle}</span>
            )}
          </div>
          <button
            type="button"
            onClick={() => handleNewHour()}
            className="inline-flex items-center justify-center rounded-md border border-transparent bg-blue-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 shrink-0"
          >
            <span className="mr-2 text-lg leading-none">+</span>
            {t("hours.sheet.add_hour")}
          </button>
        </div>

        <div className="w-full">
          <HoursViewComponent
            key="hours-view-component"
            user_id={user_id}
            onHourClick={handleHourClick}
            onNewHourClick={handleNewHour}
            showFilters={true}
            initialView={initialViewParam}
            initialDate={initialDateParam}
            initialDateFrom={initialFromParam}
            initialDateTo={initialToParam}
            initialPeriod={initialPeriodParam}
            onStateChange={({ view, date, date_from, date_to, period_type }) => {
              if (date) setCurrentViewDate(date);
              if (view === "period") {
                updateUrlParams({
                  view,
                  from: date_from,
                  to: date_to,
                  period: period_type,
                });
              } else {
                updateUrlParams({ view, date });
              }
            }}
            pushHour={lastChangedHour}
            removeHourId={deletedHourId}
          />
        </div>

        <HourEditDialog
          open={dialogOpen}
          hourId={hourFormId}
          prefill={hourFormId ? null : newHourPrefill}
          onClose={closeDialog}
          onSaved={handleSaved}
          onDeleted={handleDeleted}
        />
      </div>
    </DashboardHeader>
  );
};
